/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	NODE_RESULT_TYPE,
	IActionDebugData,
	IActionResultErrorObject,
	IEndpointParams,
	IResourceDescription
} from "@adapptio/lib-core";
import {
	createEventEmitter,
	emitEvent,
	isNonEmptyArray,
	offEvent,
	onEvent,
	removeAllEventListeners,
	TGetSimpleEventEmitterDataType,
	TSimpleEventListener
} from "@adapptio/lib-shared";
import { isSchemaFlowNodeModel, TTypeDesc } from "@adapptio/lib-blueprint";
import { IEditor } from "../../../IEditor";
import { ResourceService } from "../../../resources/ResourceService";
import { IEditorResource_Base, IEditorResource_Endpoint } from "../../../resources/ResourceTypes";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { LayoutState, LayoutState_Tabs } from "../../../ui/components/Layout/LayoutState";
import { IView, IViewEditable, IViewInfo } from "../../../view/IView";
import { EndpointEditorEditLayoutConfig } from "./Layout";
import { ENDPOINT_MODEL_MODE, EndpointModel } from "../Common/EndpointModel";
import { ViewService } from "../../../view/ViewService";
import { IntegrationService } from "../../../integration/IntegrationService";
import { IIntegrationFunction, IIntegrationType } from "../../../integration/IntegrationServiceTypes";
import { BrowserLogger } from "../../../common/BrowserLogger";
import { ABILITY_ACTIONS, AbilityService } from "../../../ability/AbilityService";
import { ICopyData, IPasteData, OnCopyData, OnPasteData } from "../../../copyPaste";
import { isSelectionItemEndpointFlowNode } from "../Common/EndpointEditorEditSelection";
import { IEditorApiService } from "../../../api/IEditorApiService";

interface IEndpointEditorViewParams {
	resourceId: string;
}

export interface IEndpointEditorViewExecutionResult {
	status?: NODE_RESULT_TYPE;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	data?: any;
	type?: TTypeDesc;
	debugData?: IActionDebugData;
	lastError: IActionResultErrorObject;
}

export enum ENDPOINT_EDITOR_VIEW_MODE {
	DESIGN = "design",
	DEBUG = "debug"
}

export class EndpointEditorEditView implements IView<IEndpointEditorViewParams>, IViewEditable {
	/* Ability */
	private abilitySvc: AbilityService;

	/* Services */
	private logger: BrowserLogger;
	private resourceSvc: ResourceService;
	private runtimeSvc: RuntimeService;
	private integrationSvc: IntegrationService;
	private viewSvc: ViewService;

	/* Resource */
	private resourceDescriptor: IResourceDescription;
	private resource: IEditorResource_Endpoint;

	/** View info change event */
	public onViewInfoChanged = createEventEmitter<void>();

	/** Emitted when resource descriptor changed */
	public onResourceInfoChanged = createEventEmitter<void>();

	/** Emitted when endpoint has been executed */
	public onExecuted = createEventEmitter<void>();

	/** Emitted when execution status changes */
	public onExecutionStatusChange = createEventEmitter<void>();

	/** Emitted when editor mode change */
	public onModeChange = createEventEmitter();

	/* INTERNAL: Emit to zoom flow editor */
	public __onFlowEditorZoomIn = createEventEmitter<void>();
	public __onFlowEditorZoomOut = createEventEmitter<void>();
	public __onFlowEditorZoomReset = createEventEmitter<void>();
	public __onFlowEditorZoomFit = createEventEmitter<void>();

	/** Document model */
	private endpointModel: EndpointModel;

	/** Layout state */
	private layout: LayoutState;

	/** Endpoint editor mode */
	private editorMode: ENDPOINT_EDITOR_VIEW_MODE = ENDPOINT_EDITOR_VIEW_MODE.DESIGN;

	/** If resource was modified */
	public isModified: boolean;

	/** If endpoint is being saved */
	private saveInProgress = false;

	/** If endpoint is being executed */
	private executionInProgress = false;

	/** Execution result */
	private executionResult: IEndpointEditorViewExecutionResult | null = null;

	private apiService: IEditorApiService;

	/* Resource event handlers */
	private handleResourceChange: () => void;
	private handleResourceDelete: TSimpleEventListener<
		TGetSimpleEventEmitterDataType<IEditorResource_Base["onDelete"]>
	>;

	/**
	 * View constructor
	 *
	 * @param dc Dependency Container
	 */
	public constructor(dc: IEditor) {
		this.abilitySvc = dc.get("abilityService");
		this.logger = dc.get("logger").facility("EndpointEditorView");
		this.resourceSvc = dc.get("resourceService");
		this.runtimeSvc = dc.get("runtimeService");
		this.integrationSvc = dc.get("integrationService");
		this.viewSvc = dc.get("viewService");
		this.apiService = dc.get("apiService");

		this.layout = new LayoutState(EndpointEditorEditLayoutConfig);
	}

	/**
	 * View init
	 *
	 * @param params Params
	 */
	public async init(params: IEndpointEditorViewParams, viewId: string): Promise<void> {
		this.resourceDescriptor = this.resourceSvc.getResourceDescriptorById(params.resourceId);
		this.resource = (await this.resourceSvc.getBlueprintById(
			params.resourceId
		)) as IEditorResource_Endpoint;

		emitEvent(this.onViewInfoChanged);

		if (!this.resource) {
			throw new Error(`Resource '${params.resourceId}' was not found.`);
		}

		this.endpointModel = new EndpointModel(this.runtimeSvc, this.integrationSvc, {});
		await this.endpointModel.loadDocument(this.resource.content);

		onEvent(this.endpointModel.onModelChange, () => {
			this.setIsModified(true, true);
		});

		onEvent(this.endpointModel.getSelection().onSelectSingle, () => {
			this.layout.getById<LayoutState_Tabs>("panelRight").selectTab(1);
		});

		onEvent(this.endpointModel.getSelection().onSelectSelected, () => {
			this.layout.getById<LayoutState_Tabs>("panelRight").selectTab(1);
		});

		onEvent(this.endpointModel.getSelection().onUnselectAll, () => {
			this.layout.getById<LayoutState_Tabs>("panelRight").selectTab(0);
		});

		onEvent(
			this.resource.onChange,
			(this.handleResourceChange = () => {
				this.resourceDescriptor = this.resource.descriptor;
				emitEvent(this.onViewInfoChanged);
				emitEvent(this.onResourceInfoChanged);
			})
		);

		onEvent(
			this.resource.onDelete,
			(this.handleResourceDelete = ({ deletedByLocalAction }) => {
				if (deletedByLocalAction || !this.isModified) {
					this.isModified = false;
					this.viewSvc.close(viewId);
				}
			})
		);
	}

	private emitViewInfoChanged(): void {
		emitEvent(this.onViewInfoChanged);
	}

	/**
	 * Returns endpoint model
	 */
	public getEndpointModel(): EndpointModel {
		return this.endpointModel;
	}

	/**
	 * Returns layout state
	 */
	public getLayout(): LayoutState {
		return this.layout;
	}

	/**
	 * Returns resource descriptor
	 */
	public getResourceDescriptor(): IResourceDescription {
		return this.resourceDescriptor;
	}

	/**
	 * Returns resource
	 */
	public getResource(): IEditorResource_Endpoint {
		return this.resource;
	}

	/**
	 * Returns current editor mode
	 */
	public getMode(): ENDPOINT_EDITOR_VIEW_MODE {
		return this.editorMode;
	}

	/**
	 * Sets the editor mode
	 *
	 * @param editorMode New editor mode
	 */
	public setMode(editorMode: ENDPOINT_EDITOR_VIEW_MODE): void {
		switch (editorMode) {
			case this.editorMode:
				return;
			case ENDPOINT_EDITOR_VIEW_MODE.DESIGN:
				this.endpointModel.setMode(ENDPOINT_MODEL_MODE.EDIT);
				break;
			default:
				break;
		}

		this.editorMode = editorMode;

		this.emitModeChange();
	}

	/**
	 * Emits event when editor mode changes
	 */
	private emitModeChange(): void {
		emitEvent(this.onModeChange);
	}

	/**
	 * Returns a list of parent resource descriptors
	 */
	public getResourceParentList(): IResourceDescription[] {
		return this.resourceSvc.getResourceParentList(this.resource.id);
	}

	/**
	 * Returns integration types
	 */
	public getIntegrationTypesList(): IIntegrationType[] {
		return this.integrationSvc.getIntegrationTypes();
	}

	/**
	 * Returns single intergration type
	 *
	 * @param integrationType Integration type name
	 */
	public getIntegrationType(integrationType: string): IIntegrationType {
		return this.integrationSvc.getIntegrationType(integrationType);
	}

	/**
	 * Returns spefici integration function
	 *
	 * @param integrationType Integration type name
	 * @param functionName Function name
	 */
	public getIntegrationFunction(integrationType: string, functionName: string): IIntegrationFunction {
		return this.integrationSvc.getSingleIntegrationFunction(integrationType, functionName);
	}

	/**
	 * Returns resource service
	 */
	public getResourceService(): ResourceService {
		return this.resourceSvc;
	}

	/**
	 * Saves changes
	 * Returns false if save is already in progress or no changes were made.
	 */
	public async save(): Promise<boolean> {
		if (!this.resource) {
			throw new Error("Resource was not loaded.");
		}

		if (!this.isModified || this.saveInProgress) {
			return false;
		}

		const content = this.endpointModel.exportDocumentAsYAML();

		this.saveInProgress = true;
		emitEvent(this.onResourceInfoChanged);

		this.setIsModified(false, false);

		try {
			await this.resourceSvc.saveBlueprint(
				this.resource.id,
				content,
				this.resource.metaData.revision,
				"Editor Save"
			);

			this.setIsModified(this.isModified, true);
		} catch (err) {
			this.setIsModified(true, true);
			throw err;
		} finally {
			this.saveInProgress = false;

			emitEvent(this.onResourceInfoChanged);
		}

		return true;
	}

	public async execute(): Promise<boolean> {
		if (this.executionInProgress || !this.resource) {
			return false;
		}

		this.executionInProgress = true;
		emitEvent(this.onExecutionStatusChange);

		try {
			const paramsData = await this.endpointModel.exportParamsData();

			const endpointParams: IEndpointParams = {
				body: paramsData.body,
				cookies: undefined,
				headers: {}, // We need to convert array into object until back-end supports correct headers format
				query: paramsData.query,
				params: paramsData.params
			};

			for (let i = 0; i < paramsData.headers.length; i++) {
				const header = paramsData.headers[i];

				if (header.name) {
					endpointParams.headers[header.name] = header.value || "";
				}
			}

			const result = await this.apiService.invokeEndpoint(this.resource.id, endpointParams, {
				debug: true,
				withTypeDescriptor: true
			});

			const error = result.status === NODE_RESULT_TYPE.ERROR ? result.error : null;
			const data = result.status === NODE_RESULT_TYPE.SUCCESS ? result.data : null;
			const typeDescriptor = result.status === NODE_RESULT_TYPE.SUCCESS ? result.typeDescriptor : null;
			const debugData = result.debug;

			this.executionResult = {
				status: result.status,
				data: data,
				lastError: error,
				debugData: debugData,
				type: typeDescriptor
			};

			if (debugData) {
				this.endpointModel.assignDebugData(debugData);
			}

			return true;
		} catch (err) {
			return false;
		} finally {
			this.executionInProgress = false;
			emitEvent(this.onExecutionStatusChange);
			emitEvent(this.onExecuted);
		}
	}

	/**
	 * Returns if endpoint is being saved
	 */
	public isBeingSaved(): boolean {
		return this.saveInProgress;
	}

	public canSave(): boolean {
		return (
			this.isModified &&
			!this.isBeingSaved() &&
			this.abilitySvc.can(ABILITY_ACTIONS.WRITE, this.resource)
		);
	}

	/**
	 * Returns if endpoint is being executed
	 */
	public isBeingExecuted(): boolean {
		return this.executionInProgress;
	}

	/**
	 * Returns last execution result object
	 */
	public getExecutionResult(): IEndpointEditorViewExecutionResult {
		return this.executionResult;
	}

	/**
	 * Zoom in the flow editor
	 */
	public flowEditorZoomIn(): void {
		emitEvent(this.__onFlowEditorZoomIn);
	}

	/**
	 * Zoom out the flow editor
	 */
	public flowEditorZoomOut(): void {
		emitEvent(this.__onFlowEditorZoomOut);
	}

	/**
	 * Reset zoom of the flow editor
	 */
	public flowEditorZoomReset(): void {
		emitEvent(this.__onFlowEditorZoomReset);
	}

	/**
	 * Fits the flow editor zoom
	 */
	public flowEditorZoomFit(): void {
		emitEvent(this.__onFlowEditorZoomFit);
	}

	@OnCopyData()
	public handleCopyData(): ICopyData {
		const endpointModel = this.getEndpointModel();
		const selection = endpointModel.getSelection();
		const dCtx = endpointModel.getDesignContext();

		const items = selection.getItems();

		const result: ICopyData = {};

		for (let i = 0; i < items.length; i++) {
			const selectionItem = items[i];

			if (isSelectionItemEndpointFlowNode(selectionItem)) {
				result.flowNodes = result.flowNodes || [];
				const model = dCtx.getNodeById(selectionItem.nodeId);

				if (isSchemaFlowNodeModel(model)) {
					result.flowNodes.push(model);
				}

				continue;
			}
		}

		return result;
	}

	@OnPasteData()
	public handlePasteData(data: IPasteData): void {
		const endpointModel = this.getEndpointModel();
		const bpSchemaModel = endpointModel.getModel();
		const dCtx = endpointModel.getDesignContext();

		const viewProps = bpSchemaModel?.props.spec.constant.props;

		const flowNodeListModel = viewProps.nodes.value;

		if (isNonEmptyArray(data.flowNodes)) {
			for (let i = 0; i < data.flowNodes.length; i++) {
				const modelIdt = data.flowNodes[i];

				const itemModel = flowNodeListModel.schema.flowNodeSchema.parse(dCtx, modelIdt, null);

				itemModel.id = `${itemModel.id}${Math.random() * 1000}`;
				let idIndex = 0;

				while (flowNodeListModel.items.some((item) => item.id === itemModel.id)) {
					idIndex = idIndex + 1;
					itemModel.id = itemModel.id + idIndex;
				}

				// eslint-disable-next-line max-len
				while (
					flowNodeListModel.items.some(
						(item) =>
							item.position.props.x.value === itemModel.position.props.x.value &&
							item.position.props.y.value === itemModel.position.props.y.value
					)
				) {
					itemModel.position.props.x.schema.setValue(
						itemModel.position.props.x,
						itemModel.position.props.x.value + 20
					);
					itemModel.position.props.y.schema.setValue(
						itemModel.position.props.y,
						itemModel.position.props.y.value + 20
					);
				}

				flowNodeListModel.schema.addItemModel(flowNodeListModel, itemModel, true);
			}
		}
	}

	public deselectAllEventNode() {
		this.endpointModel.getSelection().deselectAll();
		this.layout.getById<LayoutState_Tabs>("panelRight").selectTab(0);
	}

	/**
	 * Disposes view
	 */
	public dispose(): void {
		if (this.endpointModel) {
			this.endpointModel.dispose();
		}

		removeAllEventListeners(this.onViewInfoChanged);
		removeAllEventListeners(this.onResourceInfoChanged);
		removeAllEventListeners(this.onExecuted);
		removeAllEventListeners(this.onExecutionStatusChange);

		removeAllEventListeners(this.__onFlowEditorZoomIn);
		removeAllEventListeners(this.__onFlowEditorZoomOut);
		removeAllEventListeners(this.__onFlowEditorZoomReset);
		removeAllEventListeners(this.__onFlowEditorZoomFit);

		if (this.resource) {
			offEvent(this.resource.onChange, this.handleResourceChange);
			offEvent(this.resource.onDelete, this.handleResourceDelete);
		}

		this.layout.dispose();
	}

	private setIsModified(isModified: boolean, notify: boolean) {
		this.isModified = isModified;

		if (notify) {
			emitEvent(this.onViewInfoChanged);
		}
	}

	/**
	 * Returns if endpoint has been modified
	 */
	public getIsModified(): boolean {
		return this.isModified;
	}

	/**
	 * Returns if a current view is active
	 */
	public isViewActive(): boolean {
		return this.viewSvc.getCurrentView() === this;
	}

	/**
	 * Returns view info
	 */
	public getViewInfo(): IViewInfo {
		return {
			label: this.resourceDescriptor?.name || "...",
			icon: "mdi/hubspot",
			canClose: true,
			canSave: this.canSave(),
			isModified: this.isModified,
			renderWhenNotActive: true,
			tabStyleModifier: {
				"resource-type-endpoint": true
			}
		};
	}

	public undo() {
		this.endpointModel.undoRedoManager.undo();
		this.setIsModified(true, true);
	}

	public redo() {
		this.endpointModel.undoRedoManager.redo();
		this.setIsModified(true, true);
	}

	public canUndo() {
		return this.endpointModel.undoRedoManager.canUndo();
	}

	public canRedo() {
		return this.endpointModel.undoRedoManager.canRedo();
	}

	/**
	 * Returns related resource description
	 * @param resourceId Resource ID
	 */
	public getRelatedResourceDescrption(resourceId: string): IResourceDescription {
		return this.resourceSvc.getResourceDescriptorById(resourceId);
	}
}
